import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { USER_PROFILE } from '../config/Config';

export default function TeacherCompleteProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [useOtpLoginOnly, setUseOtpLoginOnly] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    const isStudent =
      user?.is_student ||
      user?.isStudent ||
      (user?.role_name && String(user.role_name).toLowerCase() === 'student');

    if (isStudent) {
      navigate('/student/complete-profile', { replace: true });
      return;
    }

    if (!user?.requires_profile_completion) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, user]);

  const refreshProfile = async () => {
    const profileRes = await api.get(USER_PROFILE);
    if (setUser) setUser(profileRes.data);
    if (localStorage.getItem('token')) {
      localStorage.setItem('user', JSON.stringify(profileRes.data));
    }
    if (sessionStorage.getItem('token')) {
      sessionStorage.setItem('user', JSON.stringify(profileRes.data));
    }
    return profileRes.data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required.');
      return;
    }
    if (!useOtpLoginOnly) {
      if (!password || !confirmPassword) {
        setError('Password and confirm password are required unless you choose OTP-based login.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        complete_registration_profile: true,
        use_otp_login_only: useOtpLoginOnly,
      };
      if (!useOtpLoginOnly) {
        payload.password = password;
        payload.password2 = confirmPassword;
      }

      await api.patch(USER_PROFILE, payload);
      await refreshProfile();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const data = err?.response?.data;
      const firstKey = data && typeof data === 'object' ? Object.keys(data)[0] : null;
      const firstValue = firstKey ? data[firstKey] : null;
      setError(
        data?.error ||
          data?.detail ||
          (Array.isArray(firstValue) ? firstValue[0] : firstValue) ||
          'Failed to save your profile.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
      <Paper elevation={2} sx={{ p: 3.5, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          Complete your profile
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Add your name and choose how you want to sign in. Your email stays fixed to the account you registered with.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={useOtpLoginOnly}
                onChange={(e) => setUseOtpLoginOnly(e.target.checked)}
              />
            }
            label="I will use OTP to sign in"
            sx={{ mb: 2 }}
          />

          {!useOtpLoginOnly && (
            <>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Confirm password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                sx={{ mb: 2 }}
              />
            </>
          )}

          <Button type="submit" variant="contained" disabled={submitting} fullWidth>
            {submitting ? 'Saving...' : 'Continue'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
